/**
 * Hero slider — custom element + Swiper (carousel when 2+ slides).
 * DOM hooks use data-hero-slider-* attributes (not BEM classes).
 */
const HERO_SLIDER_SELECTORS = {
  slide: '[data-hero-slider-slide]',
  viewport: '[data-hero-slider-viewport]',
  pagination: '[data-hero-slider-pagination]',
  scroll: '[data-hero-slider-scroll]',
  video: '[data-hero-slider-video]',
  videoPoster: '[data-hero-slider-video-poster]',
  contentInner: '.bst-hero-slider__content-inner',
};

const HERO_SLIDER_DESKTOP_MQ = '(min-width: 990px)';
const HERO_SLIDER_MAX_HEIGHT = 1080;

class HeroSliderComponent extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.visibilityHandler = null;
    this.heightResizeObserver = null;
    this.onScrollClick = this.onScrollClick.bind(this);
    this.onVideoCanPlay = this.onVideoCanPlay.bind(this);
    this.measureContentHeight = this.measureContentHeight.bind(this);
  }

  connectedCallback() {
    this.initScrollControl();
    this.bindContentHeight();
    this.init();
    this.syncVideoPlayback();
  }

  disconnectedCallback() {
    this.destroy();
  }

  usesContentHeight() {
    return this.classList.contains('bst-hero-slider--height-from-content');
  }

  bindContentHeight() {
    this.unbindContentHeight();

    if (!this.usesContentHeight()) return;

    this.heightResizeObserver = new ResizeObserver(this.measureContentHeight);
    this.heightResizeObserver.observe(this);
    this.querySelectorAll(HERO_SLIDER_SELECTORS.contentInner).forEach((inner) => {
      this.heightResizeObserver.observe(inner);
    });

    window.addEventListener('resize', this.measureContentHeight);

    if (document.fonts?.ready) {
      document.fonts.ready.then(this.measureContentHeight);
    }

    this.measureContentHeight();
    requestAnimationFrame(() => {
      requestAnimationFrame(this.measureContentHeight);
    });
  }

  unbindContentHeight() {
    if (this.heightResizeObserver) {
      this.heightResizeObserver.disconnect();
      this.heightResizeObserver = null;
    }

    window.removeEventListener('resize', this.measureContentHeight);
    this.clearContentHeight();
  }

  clearContentHeight() {
    this.style.removeProperty('--hero-slider-height');
    this.style.removeProperty('--hero-slider-content-height');
    this.removeAttribute('data-hero-slider-height-set');
  }

  getContentMeasureWidth(inner) {
    const content = inner?.closest('.bst-hero-slider__content');
    const shell = inner?.closest('.bst-hero-slider__content-shell');
    const box = shell || content;
    if (!box) return this.clientWidth;

    const styles = getComputedStyle(box);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;
    const width = box.clientWidth - paddingLeft - paddingRight;

    return width > 0 ? width : this.clientWidth;
  }

  measureInnerBlockHeight(inner, measureWidth) {
    if (!inner) return 0;

    /* Always measure intrinsic height — live size is flex-compressed inside the hero */
    const width = measureWidth || this.getContentMeasureWidth(inner);
    const clone = inner.cloneNode(true);

    clone.setAttribute('aria-hidden', 'true');
    clone.style.cssText = [
      'position:absolute',
      'visibility:hidden',
      'pointer-events:none',
      'height:auto',
      'width:' + width + 'px',
      'max-width:' + width + 'px',
      'left:-9999px',
      'top:0',
      'flex-shrink:0',
    ].join(';');

    this.appendChild(clone);
    const measured = Math.ceil(
      Math.max(clone.scrollHeight, clone.offsetHeight, clone.getBoundingClientRect().height)
    );
    this.removeChild(clone);

    return measured;
  }

  measureContentHeight() {
    if (!this.usesContentHeight()) return;

    if (!window.matchMedia(HERO_SLIDER_DESKTOP_MQ).matches) {
      this.clearContentHeight();
      return;
    }

    const referenceInner = this.querySelector(HERO_SLIDER_SELECTORS.contentInner);
    const measureWidth = this.getContentMeasureWidth(referenceInner);
    let maxInnerHeight = 0;

    this.querySelectorAll(
      `${HERO_SLIDER_SELECTORS.slide}:not(.swiper-slide-duplicate)`
    ).forEach((slide) => {
      const inner = slide.querySelector(HERO_SLIDER_SELECTORS.contentInner);
      maxInnerHeight = Math.max(maxInnerHeight, this.measureInnerBlockHeight(inner, measureWidth));
    });

    if (maxInnerHeight === 0) return;

    const paddingY =
      parseFloat(getComputedStyle(this).getPropertyValue('--hero-slider-padding-y')) || 110;
    /* Total height = content block + top padding + bottom padding (e.g. 560 + 110 + 110 = 780) */
    const height = Math.min(maxInnerHeight + paddingY * 2, HERO_SLIDER_MAX_HEIGHT);

    this.style.setProperty('--hero-slider-content-height', `${maxInnerHeight}px`);
    this.style.setProperty('--hero-slider-height', `${height}px`);
    this.setAttribute('data-hero-slider-height-set', '');

    if (this.swiper) {
      this.swiper.update();
    }
  }

  get slideCount() {
    return this.querySelectorAll(HERO_SLIDER_SELECTORS.slide).length;
  }

  get isCarousel() {
    return this.slideCount > 1;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  initScrollControl() {
    const scrollBtn = this.querySelector(HERO_SLIDER_SELECTORS.scroll);
    if (!scrollBtn) return;

    scrollBtn.removeEventListener('click', this.onScrollClick);
    scrollBtn.addEventListener('click', this.onScrollClick);
  }

  onScrollClick() {
    const shopifySection = this.closest('.shopify-section');
    let nextSection = shopifySection?.nextElementSibling;

    while (
      nextSection &&
      !nextSection.classList.contains('shopify-section') &&
      nextSection.tagName !== 'SECTION'
    ) {
      nextSection = nextSection.nextElementSibling;
    }

    if (!nextSection) {
      const main = document.getElementById('MainContent');
      const sections = main?.querySelectorAll('.shopify-section');
      if (sections?.length) {
        const currentIndex = Array.from(sections).indexOf(shopifySection);
        if (currentIndex >= 0 && currentIndex < sections.length - 1) {
          nextSection = sections[currentIndex + 1];
        }
      }
    }

    if (nextSection) {
      nextSection.scrollIntoView({
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    }
  }

  getActiveSlide() {
    if (this.swiper) {
      return this.swiper.slides[this.swiper.activeIndex];
    }
    return this.querySelector(HERO_SLIDER_SELECTORS.slide);
  }

  syncVideoPlayback() {
    const activeSlide = this.getActiveSlide();

    this.querySelectorAll(HERO_SLIDER_SELECTORS.video).forEach((video) => {
      const isActive = activeSlide?.contains(video);

      if (isActive) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      } else {
        video.pause();
      }
    });

    this.querySelectorAll(HERO_SLIDER_SELECTORS.slide).forEach((slide) => {
      const isActive = slide === activeSlide;
      const poster = slide.querySelector(HERO_SLIDER_SELECTORS.videoPoster);
      const embed = slide.querySelector('[data-hero-slider-video-embed]');

      if (!poster) return;

      if (!isActive) {
        poster.hidden = false;
        clearTimeout(poster._hideTimer);
        return;
      }

      if (embed) {
        poster.hidden = false;
        clearTimeout(poster._hideTimer);
        poster._hideTimer = setTimeout(() => {
          poster.hidden = true;
        }, 600);
      } else {
        poster.hidden = true;
      }
    });
  }

  onVideoCanPlay(event) {
    const video = event.target;
    const activeSlide = this.getActiveSlide();
    if (activeSlide?.contains(video)) {
      video.play().catch(() => {});
      const poster = activeSlide.querySelector(HERO_SLIDER_SELECTORS.videoPoster);
      if (poster) poster.hidden = true;
    }
  }

  bindVideos() {
    this.querySelectorAll(HERO_SLIDER_SELECTORS.video).forEach((video) => {
      video.removeEventListener('canplay', this.onVideoCanPlay);
      video.addEventListener('canplay', this.onVideoCanPlay);
      video.muted = true;
    });
  }

  init() {
    this.destroy({ keepHeightListeners: true });
    this.bindVideos();

    if (!this.isCarousel) {
      this.syncVideoPlayback();
      this.measureContentHeight();
      return;
    }

    const swiperEl = this.querySelector(HERO_SLIDER_SELECTORS.viewport);
    if (!swiperEl || typeof Swiper === 'undefined') {
      this.measureContentHeight();
      return;
    }

    const slideCount = this.slideCount;
    const autoplayEnabled =
      this.dataset.autoplay === 'true' && !this.prefersReducedMotion();
    const autoplayDelay = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
    const paginationEl = this.querySelector(HERO_SLIDER_SELECTORS.pagination);
    const reducedMotion = this.prefersReducedMotion();
    const mobileStacked =
      this.classList.contains('bst-hero-slider--mobile-stacked') &&
      window.matchMedia('(max-width: 989px)').matches;

    const config = {
      slidesPerView: 1,
      loop: slideCount > 1,
      speed: reducedMotion ? 0 : 600,
      effect: 'fade',
      fadeEffect: { crossFade: true },
      watchOverflow: true,
      autoHeight: mobileStacked,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        paginationBulletMessage: 'Go to slide {{index}}',
      },
      keyboard: { enabled: true, onlyInViewport: true },
      on: {
        init: () => this.syncVideoPlayback(),
        slideChangeTransitionEnd: () => this.syncVideoPlayback(),
      },
    };

    if (paginationEl) {
      config.pagination = {
        el: paginationEl,
        clickable: true,
        bulletElement: 'button',
        renderBullet(index, className) {
          return (
            '<button type="button" class="' +
            className +
            '" aria-label="Go to slide ' +
            (index + 1) +
            ' of ' +
            slideCount +
            '"></button>'
          );
        },
      };
    }

    if (autoplayEnabled) {
      config.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      };
    }

    this.swiper = new Swiper(swiperEl, config);
    this.syncVideoPlayback();
    this.measureContentHeight();

    if (autoplayEnabled && this.swiper.autoplay) {
      this.visibilityHandler = () => {
        if (document.hidden) {
          this.swiper.autoplay.stop();
        } else {
          this.swiper.autoplay.start();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  destroy(options = {}) {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (!options.keepHeightListeners) {
      this.unbindContentHeight();
    }
  }
}

customElements.define('hero-slider-component', HeroSliderComponent);
